import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { list_events, create_event, update_event, delete_event, get_event, count_events, get_event_illustration, get_event_document } from '../../api/functions';
import { useApi } from '../../api/api';
import { LoadingSpinner, AdminEndpoint } from '../../components';
import './TabContent.css';

const EventsTab = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const listEventsApi = useApi(list_events);
  const countEventsApi = useApi(count_events);
  const createEventApi = useApi(create_event);
  const updateEventApi = useApi(update_event);
  const deleteEventApi = useApi(delete_event);
  const getEventApi = useApi(get_event);
  const getEventIllustrationApi = useApi(get_event_illustration);
  const getEventDocumentApi = useApi(get_event_document);
  const gridRef = useRef(null);

  const [editingEvent, setEditingEvent] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copyFiles, setCopyFiles] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [allEvents, setAllEvents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);
  const [totalEventsCount, setTotalEventsCount] = useState(0);

  const BATCH_SIZE = 50;
  const ROWS_PER_PAGE = 4;

  const fetchEvents = useCallback(async () => {
    const countResult = await countEventsApi.execute({
      show_private: true,
      search_query: searchQuery || undefined,
      time_filter: 'all'
    });

    if (countResult && countResult.count !== undefined) {
      setTotalEventsCount(countResult.count);
    }

    const params = {
      skip: 0,
      limit: BATCH_SIZE,
      show_private: true,
      search_query: searchQuery || undefined,
      time_filter: 'all'
    };

    const data = await listEventsApi.execute(params);

    if (data) {
      setAllEvents(data);
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadMoreEvents = useCallback(async () => {
    const data = await listEventsApi.execute({
      skip: allEvents.length,
      limit: BATCH_SIZE,
      show_private: true,
      search_query: searchQuery || undefined,
      time_filter: 'all'
    });
    if (data) {
      setAllEvents([...allEvents, ...data]);
    }
  }, [allEvents, searchQuery]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchEvents();
    }
  }, [searchQuery, fetchEvents]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchEvents();
    }
  }, [fetchEvents]);

  useEffect(() => {
    const calculateColumns = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const minCardWidth = 280;
        const gap = 24;
        const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
        const newItemsPerPage = ROWS_PER_PAGE * columns;
        setItemsPerPage(newItemsPerPage);
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  useEffect(() => {
    const neededItems = currentPage * itemsPerPage;
    const hasMore = allEvents.length < totalEventsCount;
    if (neededItems > allEvents.length && hasMore && !listEventsApi.loading) {
      loadMoreEvents();
    }
  }, [currentPage, allEvents.length, totalEventsCount, listEventsApi.loading, itemsPerPage]);

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(totalEventsCount / itemsPerPage);
    if (totalEventsCount > 0 && calculatedTotalPages > 0 && currentPage > calculatedTotalPages) {
      setCurrentPage(calculatedTotalPages);
    } else if (totalEventsCount === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [totalEventsCount, itemsPerPage, currentPage]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new');

    if (isCreatingPath) {
      setIsCreating(true);
      setEditingEvent(null);
    } else if (id && currentPath.includes('/edit')) {
      setCopyFiles({});
      const fetchEventForEdit = async () => {
        const event = await getEventApi.execute({ event_id: parseInt(id) });
        if (event) {
          setEditingEvent(event);
          setIsCreating(false);
        }
      };
      fetchEventForEdit();
    } else {
      setIsCreating(false);
      setEditingEvent(null);
      setCopyFiles({});
    }
  }, [id, params]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedEvents = allEvents.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalEventsCount / itemsPerPage);

  const handleCreate = () => {
    navigate('/admin/events/new');
  };

  const handleEdit = (event) => {
    navigate(`/admin/events/${event.id}/edit`);
  };

  const handleCancel = () => {
    navigate('/admin/events');
  };

  const storageKey = isCreating ? 'admin_events_create' : (id ? `admin_events_edit_${id}` : null);

  const handleCopy = async (event) => {
    const copyData = {
      title_en: event.title_en || '', title_cz: event.title_cz || '', title_de: event.title_de || '',
      text_en: event.text_en || '', text_cz: event.text_cz || '', text_de: event.text_de || '',
      event_start_date: event.event_start_date || '', event_end_date: event.event_end_date || '',
      event_start_time: event.event_start_time || '', event_end_time: event.event_end_time || '',
      location_name: event.location_name || '', location_address: event.location_address || '',
      private: event.private || false,
    };

    const files = {};
    if (event.illustration_filename) {
      const blob = await getEventIllustrationApi.execute({ event_id: event.id });
      if (blob) files.illustration = new File([blob], event.illustration_filename, { type: blob.type });
    }
    if (event.document_filename) {
      const blob = await getEventDocumentApi.execute({ event_id: event.id });
      if (blob) files.document = new File([blob], event.document_filename, { type: blob.type });
    }
    setCopyFiles(files);

    localStorage.setItem('admin_events_create', JSON.stringify(copyData));
    navigate('/admin/events/new');
  };

  const handleSubmit = async (formData) => {
    if (isCreating) {
      const result = await createEventApi.execute(formData);

      if (createEventApi.error) {
        toast.error(createEventApi.error || 'An error occurred while creating the event', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Event created successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchEvents();
        navigate('/admin/events');
      }
    } else if (editingEvent) {
      const result = await updateEventApi.execute({
        ...formData,
        event_id: editingEvent.id
      });

      if (updateEventApi.error) {
        toast.error(updateEventApi.error || 'An error occurred while updating the event', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Event updated successfully!', {
          position: "bottom-right"
        });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchEvents();
        navigate('/admin/events');
      }
    }
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      const result = await deleteEventApi.execute({ event_id: eventId });

      if (deleteEventApi.error) {
        toast.error(deleteEventApi.error || 'An error occurred while deleting the event', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Event deleted successfully!', {
          position: "bottom-right"
        });
        await fetchEvents();
        navigate('/admin/events');
      }
    }
  };

  const getLocalizedField = (event, fieldPrefix) => {
    const language = i18n.language;
    if (event[`${fieldPrefix}_${language}`]) {
      return event[`${fieldPrefix}_${language}`];
    }
    if (event[`${fieldPrefix}_cz`]) {
      return event[`${fieldPrefix}_cz`];
    }
    if (event[`${fieldPrefix}_en`]) {
      return event[`${fieldPrefix}_en`];
    }
    if (event[`${fieldPrefix}_de`]) {
      return event[`${fieldPrefix}_de`];
    }
    return '';
  };

  const eventFields = [
    {
      type: 'language_tabs',
      name: 'content_tabs',
      languages: ['en', 'cz', 'de'],
      fields: [
        { name: 'title', label: t('admin.fields.title'), type: 'text', required: false, placeholder: t('admin.placeholders.title') },
        { name: 'text', label: t('admin.fields.description'), type: 'markdown', required: false, placeholder: t('admin.placeholders.description') }
      ]
    },
    { name: 'event_start_date', label: t('admin.fields.startDate'), type: 'date', required: true },
    { name: 'event_end_date', label: t('admin.fields.endDate'), type: 'date', required: true },
    { name: 'event_start_time', label: t('admin.fields.startTime'), type: 'time', required: true },
    { name: 'event_end_time', label: t('admin.fields.endTime'), type: 'time', required: true },
    { name: 'location_name', label: t('admin.fields.locationName'), type: 'text', placeholder: t('admin.placeholders.locationName') },
    { name: 'location_address', label: t('admin.fields.locationAddress'), type: 'text', placeholder: t('admin.placeholders.locationAddress') },
    // { name: 'location_latitude', label: t('admin.fields.latitude'), type: 'number', step: '0.000001', placeholder: 'e.g. 50.0755' },
    // { name: 'location_longitude', label: t('admin.fields.longitude'), type: 'number', step: '0.000001', placeholder: 'e.g. 14.4378' },
    { name: 'private', label: t('admin.fields.membersOnly'), type: 'boolean', defaultValue: false },
    { name: 'illustration', label: t('admin.fields.eventImage'), type: 'file', accept: 'image/*', currentFile: editingEvent?.illustration_filename || (isCreating ? copyFiles.illustration?.name : undefined) },
    { name: 'document', label: t('admin.fields.attachment'), type: 'file', accept: '.pdf,.doc,.docx', currentFile: editingEvent?.document_filename || (isCreating ? copyFiles.document?.name : undefined) }
  ];

  if (isCreating || editingEvent) {
    return (
      <div className="tab-container">
        <div className="tab-header">
          <h2 className="tab-title">{isCreating ? t('admin.events.createTitle') : t('admin.events.editTitle')}</h2>
        </div>

        <div className="edit-actions-top">
          <button className="cancel-btn-top" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          {editingEvent && (
            <button className="copy-btn-top" onClick={() => handleCopy(editingEvent)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t('common.copy', 'Copy')}
            </button>
          )}
          {editingEvent && (
            <button
              className="delete-btn"
              onClick={() => handleDelete(editingEvent.id)}
              disabled={deleteEventApi.loading}
            >
              {deleteEventApi.loading ? t('common.deleting') : t('admin.events.delete')}
            </button>
          )}
        </div>

        <AdminEndpoint
          fields={eventFields}
          onSubmit={handleSubmit}
          onCancel={null}
          initialData={isCreating ? { ...copyFiles } : (editingEvent || {})}
          submitLabel={isCreating ? t('admin.events.createBtn') : t('admin.events.updateBtn')}
          isLoading={createEventApi.loading || updateEventApi.loading}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">{t('admin.events.title')}</h2>
        </div>
        <button className="create-btn" onClick={handleCreate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.events.createBtn')}
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder={t('admin.events.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {listEventsApi.loading && allEvents.length === 0 ? (
        <LoadingSpinner message={t('common.loading', 'Loading events...')} />
      ) : allEvents.length === 0 ? (
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
            <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" />
            <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" />
            <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
          </svg>
          <p>{t('admin.events.noEvents')}</p>
          <button className="create-btn-secondary" onClick={handleCreate}>
            {t('admin.events.createBtn')}
          </button>
        </div>
      ) : (
        <>
          <div className="items-grid" ref={gridRef} style={{ position: 'relative', minHeight: listEventsApi.loading ? '400px' : 'auto' }}>
            {listEventsApi.loading && allEvents.length > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px'
              }}>
                <LoadingSpinner message={t('common.loading', 'Loading...')} />
              </div>
            )}
            {displayedEvents.map(event => (
              <div key={event.id} className="item-card" onClick={() => handleEdit(event)}>
                <div className="item-card-body">
                  <div className="item-id">#{event.id}</div>
                  <h3 className="item-title">{getLocalizedField(event, 'title') || 'Untitled Event'}</h3>
                  <div className="item-meta">
                    <span className="item-date">
                      {new Date(event.event_start_date).toLocaleDateString()}
                    </span>
                    {event.private && <span className="item-badge">Members Only</span>}
                  </div>
                </div>
                <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="item-copy-btn" onClick={() => handleCopy(event)} title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    Copy
                  </button>
                  <button className="item-delete-btn" onClick={() => handleDelete(event.id)} title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EventsTab;
